/**
 * Playing a storyboard back (well/research/16, C2): the panels one at a
 * time with a slow push, each caption lighting word by word as it was said.
 * Pure, so the timing is tested without a browser.
 */
export interface SpokenWord { text: string; start: number; end: number }
export interface StoryCaption { quote: string; start: number; end: number; words: SpokenWord[] }
/** Where one of the narrator's lines sits in the narration, in seconds. */
export interface Segment { start: number; end: number }

/** One screen of the playback: a panel, or the end card. Words light up at `at`. */
export interface Beat { kind: 'panel' | 'end'; index: number; from: number; to: number; words: { text: string; at: number }[] }
export interface Timeline { beats: Beat[]; total: number }

/** A panel comes up this long before its words are said. */
const LEAD = 0.4;
/** Silent playback: how long a caption stays after its last word, and the end card after its own. */
const HOLD = 1.6;
const END_HOLD = 2.8;

/**
 * When each panel is up and when each word lights, in seconds.
 *
 * With their voice the clock is the recording: a panel comes up just before
 * its quote is said and stays until the next one's is, so the drawings keep
 * pace with the story and nothing is cut. Without it the story is told in
 * captions only, so the gaps between quotes are taken out and each caption
 * plays at the speed it was spoken, then holds.
 */
export function timeline(sb: { panels?: StoryCaption[]; ending?: StoryCaption; duration?: number }, voice: boolean): Timeline {
  const panels = sb.panels || [];
  const beats: Beat[] = [];
  if (voice) {
    panels.forEach((p, i) => {
      const from = i === 0 ? 0 : Math.max(beats[i - 1].from + 0.5, p.start - LEAD);
      if (i > 0) beats[i - 1].to = from;
      beats.push({ kind: 'panel', index: i, from, to: Math.max(p.end + HOLD, sb.duration ?? 0), words: p.words.map((w) => ({ text: w.text, at: w.start })) });
    });
    const last = beats[beats.length - 1];
    const lastEnd = panels[panels.length - 1]?.end ?? 0;
    const e = sb.ending;
    if (e && e.words.length) {
      const from = Math.max(lastEnd + 0.3, e.start - LEAD, last ? last.from + 0.5 : 0);
      if (last) last.to = from;
      // The last line is often inside the last panel's quote, already said by
      // the time the card is up: it lights through again, quickly, instead of
      // waiting on words that are over.
      const said = e.start < from;
      const words = said ? e.words.map((w, k) => ({ text: w.text, at: from + 0.3 + k * 0.2 })) : e.words.map((w) => ({ text: w.text, at: w.start }));
      beats.push({ kind: 'end', index: 0, from, to: Math.max(sb.duration ?? 0, words[words.length - 1].at + END_HOLD), words });
    }
  } else {
    let t = 0;
    panels.forEach((p, i) => {
      const shift = t + LEAD - p.start;
      const to = t + LEAD + (p.end - p.start) + HOLD;
      beats.push({ kind: 'panel', index: i, from: t, to, words: p.words.map((w) => ({ text: w.text, at: w.start + shift })) });
      t = to;
    });
    const e = sb.ending;
    if (e && e.words.length) {
      const shift = t + LEAD - e.start;
      beats.push({ kind: 'end', index: 0, from: t, to: t + LEAD + (e.end - e.start) + END_HOLD, words: e.words.map((w) => ({ text: w.text, at: w.start + shift })) });
    }
  }
  return { beats, total: beats.length ? beats[beats.length - 1].to : 0 };
}

/**
 * Playback over the narrator (owner, 2026-09-28: soundtrack option A). The
 * narrator reads one line per panel and one for the ending, so each page
 * turns just before its line starts and the caption — their own words —
 * is up whole. The narrator's words are a retelling, not theirs, so nothing
 * is lit word by word.
 */
export function narratedTimeline(sb: { panels?: StoryCaption[]; ending?: StoryCaption }, segments: Segment[], seconds: number): Timeline {
  const panels = sb.panels || [];
  const beats: Beat[] = [];
  // The end card is only its words: one with none to show plays as a blank page
  // (P6, 2026-10-02), so it falls back to the quote, and with no quote either
  // the last panel holds through the narrator's last line instead.
  const endWords = sb.ending?.words?.length ? sb.ending.words : (sb.ending?.quote || '').split(/\s+/).filter(Boolean).map((text) => ({ text }));
  const kinds = [...panels.map((p, i) => ({ kind: 'panel' as const, index: i, words: p.words as { text: string }[] })), ...(endWords.length ? [{ kind: 'end' as const, index: 0, words: endWords }] : [])];
  kinds.slice(0, segments.length).forEach((k, i) => {
    const from = i === 0 ? 0 : Math.max(beats[i - 1].from + 0.5, segments[i].start - 0.25);
    if (i > 0) beats[i - 1].to = from;
    beats.push({ kind: k.kind, index: k.index, from, to: Math.max(segments[i].end + 0.6, seconds), words: k.words.map((w) => ({ text: w.text, at: from })) });
  });
  const last = beats[beats.length - 1];
  if (last) last.to = Math.max(last.to, seconds + 1.2);
  return { beats, total: last ? last.to : 0 };
}

/** The beat on screen at time t (the last one once it is over). */
export function beatAt(tl: Timeline, t: number): Beat | null {
  if (tl.beats.length === 0) return null;
  return tl.beats.find((b) => t >= b.from && t < b.to) ?? (t < tl.beats[0].from ? tl.beats[0] : tl.beats[tl.beats.length - 1]);
}

/** Where panel i sits on the 2×2 page, as a background position. */
export function quadrant(i: number): string {
  return ['0% 0%', '100% 0%', '0% 100%', '100% 100%'][i] ?? '0% 0%';
}

/** Longer captions get a smaller hand, so a twelve-word quote still fits its band. */
export function captionSize(quote: string): 'l' | 'm' | 's' {
  const n = quote.split(/\s+/).filter(Boolean).length;
  return n <= 5 ? 'l' : n <= 9 ? 'm' : 's';
}
